/* eslint-disable max-lines */
import { comparer } from 'mobx';

import {
  AnalyticsStore,
  codeSplit,
  ContextStore,
  Controller,
  ExperimentStore,
  FetchOptions,
  FetchPageOptions,
  merge,
  PageStore,
} from 'bernie-core';
import { Localization } from 'bernie-l10n';
import { PageData } from 'bernie-http';

import { CollectionBusinessModel, getPackageType } from 'stores/multi-item-store-util';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { evaluateAndLogExperiment } from 'stores/utils';
import { MultiItemStore } from 'stores/multi-item-store';
import { PageLayoutStore } from 'src/common/stores/page-layout-store';
import { SearchCriteriaModel, SearchCriteriaUtils, singleUseQueryParams, untrackedQueryParams } from 'stores/models';

import { InfositePageName } from 'constants/pages';
import { MarketingChannels } from 'constants/marketing-channels';
import { PackageType } from 'models/multi-item/multi-item-types';

import { getTrackingInfoForLogging } from 'utils/logging-utils';
import { meta } from 'utils/meta';
import { processClientSideDefaultRequestFields } from 'controllers/hotel/shared-controller-modules/utils';

import { CONTROLLER_EVENTS } from 'controllers/events/controller-events';

const { withoutKeys, withKeys } = merge;

interface ContextWithSeoData extends ContextStore {
  seoData: {
    url: string;
  };
}

export class InfositeController implements Controller {
  public bundles = ['infosite'];
  public component = codeSplit(() => import(/* webpackChunkName: "infosite" */ 'views/hotel/infosite.tsx'));
  public pageId = `${InfositePageName.SA},H,30`;
  public path = '*/:lang?/:prefix?*h(o)?:hotelId(\\d+)(.)?*';
  public exact = false;
  public routeName = 'infosite';
  public routeData = {
    rfrrNamespace: 'HOT.HIS',
    pageName: InfositePageName.SA,
  };

  public fetchPageData(options: FetchPageOptions): Promise<PageData> {
    const context = options.context as ContextWithSeoData;
    const seoData = context && context.seoData;
    const packageType = getPackageType(options.query);
    const packagePageData = packageType ? this.getPackagePageData(packageType) : { navigationId: 'hotel' };

    const pageData = {
      ...packagePageData,
    };

    // Use blacksmith to add a reference to dateless version of pdp
    if (seoData) {
      const seo = {
        seo: {
          canonical: seoData.url,
        },
      };

      return Promise.resolve({
        ...seo,
        ...pageData,
      } as PageData);
    }

    return Promise.resolve(pageData as PageData);
  }

  private getPackagePageData(packageType: PackageType) {
    const pageName = InfositePageName[packageType];
    const pageId = `${pageName},P,30`;

    return {
      pageName,
      pageId,
      navigationId: 'package',
    };
  }

  // eslint-disable-next-line complexity
  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('InfositeController.fetch received invalid FetchOptions');
    }

    const { isServer, stores, query, params } = options;
    const fetchStartTime = Date.now();

    const context: ContextStore = stores.get<ContextStore>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const currentHotel: CurrentHotelStore = stores.get<CurrentHotelStore>('currentHotel');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');
    const multiItemBusinessModel: CollectionBusinessModel = multiItem && multiItem.cartInfo && multiItem.cartInfo.type;
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const experiments: ExperimentStore = stores.get<ExperimentStore>('experiment');
    const pageLayoutStore: PageLayoutStore = stores.get<PageLayoutStore>('pageLayout');

    multiItem.isMixMatchEnabled = query.mixMatch === 'true';
    multiItem.buildContext(query);

    const locationScoreExperiment = experiments.exposures.PWA_UGC_Location_Score;
    currentHotel.shouldFetchLocationScore = locationScoreExperiment && locationScoreExperiment.bucket === 1;

    // infosite cares about hotelId URL param
    const { prevParams, ...nextParams } = params || { prevParams: undefined };

    // query params cleanup
    const singleUseCriteria: any = withKeys(singleUseQueryParams).merge({}, options.query);
    const untrackedKeys = [...untrackedQueryParams, ...singleUseCriteria, 'prevQuery'];
    const prevQuery = withoutKeys(untrackedKeys).merge({}, query.prevQuery);
    const nextQuery: any = withoutKeys(untrackedKeys).merge({}, query);

    if (multiItemBusinessModel && nextQuery) {
      nextQuery.businessModelFilter = multiItemBusinessModel;
    }

    let newQuery = processClientSideDefaultRequestFields(isServer, currentHotel, nextQuery);
    if (newQuery) {
      return newQuery;
    }

    currentHotel.searchCriteria = new SearchCriteriaModel(SearchCriteriaUtils.ensureCorrectTypes(currentHotel.searchCriteria));

    // Fetch Template details for React View Composer
    const pageLayoutTemplatePromise = pageLayoutStore.fetchPDP(nextParams.hotelId, context, multiItem.isPackageShoppingPath());

    const forceFetch = currentHotel.shouldFetch || currentHotel.shouldFetchOffers;

    if (forceFetch || !comparer.structural(nextParams, prevParams) || !comparer.structural(prevQuery, nextQuery)) {
      // TEMP FIX: check removal after 2018-05-01 with @dboerner
      this.removeRegionIdForSEMUrls(nextQuery);
      // =====================================================

      const parsedInfositeValues = SearchCriteriaUtils.ensureCorrectTypes(
        SearchCriteriaUtils.parseInfositeValues({ ...nextQuery }, new Localization(context.locale), context.localDateFormat)
      );
      const incomingSearchCriteria: any = new SearchCriteriaModel(parsedInfositeValues);

      if (incomingSearchCriteria.selectedRoomType && incomingSearchCriteria.selectedRatePlan) {
        currentHotel.selectedRoomType = incomingSearchCriteria.selectedRoomType;
        currentHotel.selectedRatePlan = incomingSearchCriteria.selectedRatePlan;
      }

      if (incomingSearchCriteria.mipt) {
        currentHotel.multiItemPriceToken = incomingSearchCriteria.mipt;
      }

      currentHotel.srpDisplayPrice = CurrentHotelStore.DEFAULTS.srpDisplayPrice;
      if (incomingSearchCriteria.top_dp && incomingSearchCriteria.top_cur) {
        currentHotel.srpDisplayPrice = Number(incomingSearchCriteria.top_dp);
        currentHotel.srpCurrency = incomingSearchCriteria.top_cur;
      }

      if (incomingSearchCriteria.rfrr) {
        currentHotel.referrer = Array.isArray(incomingSearchCriteria.rfrr)
          ? incomingSearchCriteria.rfrr.join()
          : incomingSearchCriteria.rfrr;
      }

      if (incomingSearchCriteria.pwa_ts) {
        currentHotel.offerTimestamp = incomingSearchCriteria.pwa_ts;
      }

      const criteriaChanged = this.haveCriteriaChanged(currentHotel.searchCriteria, incomingSearchCriteria, Object.keys(MarketingChannels));

      // 33486_PWA_Large_Party_Mix_Match reset cart if rooms or dates criteria changes
      const mixMatchCriteriaChanged = this.hasRoomsOrDatesChanged(currentHotel.searchCriteria, incomingSearchCriteria);

      if (multiItem && multiItem.showMixMatchExperience && mixMatchCriteriaChanged) {
        multiItem.emptyCart(context).then(() => {
          if (analyticsStore) {
            analyticsStore.trackEvent('MixMatch.EmptyCart', 'MixMatch Cart Emptied', null, null, true);
          }
        });
      }

      // On page load during client-side rendering, it's essential to set loadingOffers to false because we already have the data in the state,
      // By doing so the actual offers listings cards will render from shared-ui instead of placeholder cards.
      if (!isServer) {
        currentHotel.loadingOffers = false;
      }

      if (forceFetch || criteriaChanged || this.hasHotelChanged(prevParams, nextParams)) {
        currentHotel.setOfferData(withKeys(['startDate', 'endDate', 'adults', 'children']).merge({}, parsedInfositeValues));

        currentHotel.updateSearchCriteria(incomingSearchCriteria);
        currentHotel.setHotelId(nextParams.hotelId);
        currentHotel.clearPoiData();

        if (isServer) {
          return;
        }

        await this.fetchInfo(options, fetchStartTime, singleUseCriteria).catch(() => {
          if (options.logger) {
            options.logger.logEvent(
              CONTROLLER_EVENTS.INFOSITE_CONTROLLER_FETCH_FAILURE,
              `infosite controller unhandled failure in FetchInfo ${getTrackingInfoForLogging(context)}`
            );
          }
        });
      }
    }

    if (
      !currentHotel.searchCriteria.isDateless &&
      (!currentHotel.similarHotels || !comparer.structural(nextParams, prevParams) || !comparer.structural(prevQuery, nextQuery))
    ) {
      if (!multiItem.isPackageShoppingPath() || evaluateAndLogExperiment('travelad_carousel_hc_pdp', context, experiments, page) === 1) {
        currentHotel.fetchSimilarHotels(context, multiItem.multiItemContext, page.pageId, experiments);
      }
    }

    await pageLayoutTemplatePromise;

    newQuery = processClientSideDefaultRequestFields(isServer, currentHotel, nextQuery);
    if (newQuery) {
      return newQuery;
    }
  }

  // eslint-disable-next-line complexity
  public async fetchInfo(options: FetchOptions, fetchStartTime: number, singleUseCriteria: any): Promise<any> {
    const { stores, logger, isServer } = options;

    const context: ContextStore = stores.get<ContextStore>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const currentHotel: CurrentHotelStore = stores.get<CurrentHotelStore>('currentHotel');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');

    const currentHotelFetch = currentHotel.fetch(analyticsStore, context, multiItem, singleUseCriteria);
    const locationScoreFetch =
      currentHotel &&
      currentHotel.shouldFetchLocationScore &&
      currentHotel.fetchPropertyLocationScoreInfo(context, currentHotel.hotelId, page.pageId);

    // The response to the PropertyRoomScoreInfo graphql query remains the same for a property,
    // so we don't need to keep making this call every time search criteria (dates, travelers etc) changes on PDP.
    if (!currentHotel?.sharedUiPropertyReviewAggregatesResponse?.propertySummaries?.length) {
      const roomScoreFetch = currentHotel && currentHotel.fetchPropertyRoomScoreInfo(context, currentHotel.hotelId, page.pageId);

      if (roomScoreFetch) {
        await roomScoreFetch;
      }
    }

    await currentHotelFetch;

    // During server side rendering, we want to set loadingOffers to true,
    // so that the vertical placeholder cards will be rendered from shared-ui
    if (isServer) {
      currentHotel.loadingOffers = true;
    }

    if (logger) {
      logger.logEvent(
        CONTROLLER_EVENTS.INFOSITE_CONTROLLER_FETCH,
        `fetchTime=${(Date.now() - fetchStartTime).toString()} ms`,
        `, isServer=${isServer}, ${getTrackingInfoForLogging(context)}`
      );
    }

    if (locationScoreFetch) {
      await locationScoreFetch;
    }

    if (!page.seo) {
      const pageFetch = page.fetch(meta['hotel-infosite'], context.site, context.locale);
      await pageFetch;
    }
  }

  public async willDeactivate(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('InfositeController.willDeactivate receieved an invalid StoreContainer');
    }

    const currentHotel: CurrentHotelStore = options.stores.get<CurrentHotelStore>('currentHotel');

    if (currentHotel.isFailure || !currentHotel.offers || currentHotel.offers.error) {
      currentHotel.shouldFetch = true;
    }

    currentHotel.clearRoomsFromOffers();
    currentHotel.shouldFetchOffers = true;
    currentHotel.loadingOffers = true;
  }

  private removeRegionIdForSEMUrls(query): any {
    // TODO: SEM regionId fix (do not remove until SEM URLs have been fixed)
    if (query && /google/gi.test(query.semcid)) {
      delete query.regionId;
    }
  }

  private hasHotelChanged(prevParams, nextParams): boolean {
    if (prevParams && nextParams && prevParams.hotelId && nextParams.hotelId) {
      return prevParams.hotelId !== nextParams.hotelId;
    }

    return false;
  }

  private haveCriteriaChanged(previousModel: SearchCriteriaModel, newModel: SearchCriteriaModel, ignoredKeys: string[]): boolean {
    if (Boolean(newModel) && !previousModel) {
      return true;
    }

    return !comparer.structural(previousModel.toJSON(ignoredKeys), newModel.toJSON(ignoredKeys));
  }

  private hasRoomsOrDatesChanged(previousModel: SearchCriteriaModel, newModel: SearchCriteriaModel): boolean {
    if (newModel && !previousModel) {
      return true;
    }
    const startDateChanged = previousModel.startDate !== newModel.startDate;
    const endDateChanged = previousModel.endDate !== newModel.endDate;

    return !comparer.structural(previousModel.rooms, newModel.rooms) || startDateChanged || endDateChanged;
  }
}
// tslint:disable-next-line: max-file-line-count
